<template>
    <UnitDetailLayout
        kind="role"
        :entity="role || {}"
        :collaborators="collaborators"
        :course-rows="courseRows"
        :completion-avg="completionAvg"
        :loading="loading"
        :error="error"
        :error-message="error === 'not_found' ? t.roleDetail.notFound : t.common.error"
        :breadcrumbs="breadcrumbs"
        :title="role?.name || t.roleDetail.title"
        :subtitle="t.roleDetail.pageLead"
        :labels="layoutLabels"
        @back="goBack"
        @open-collaborator="goCollaborator"
        @open-sector="goSector"
    />
</template>

<script>
import UnitDetailLayout from '@/components/Admin/Corporate/UnitDetailLayout.vue';
import rolesApi from '@/utils/corporate/roles';
import usersApi from '@/utils/corporate/users';
import { aggregateCourses } from '@/utils/corporate/progressAggregate';
import { corporateI18n } from '@/i18n/mixins';
import { mapState } from 'vuex';

export default {
    name: 'CorporateRoleDetail',
    mixins: [corporateI18n],
    components: { UnitDetailLayout },
    data() {
        return {
            loading: true,
            error: false,
            role: null,
            collaborators: [],
            courseRows: [],
            stats: null,
        };
    },
    computed: {
        ...mapState(['token']),
        completionAvg() {
            if (this.stats && typeof this.stats.completionAvg === 'number') {
                return this.stats.completionAvg;
            }
            if (!this.collaborators.length) return 0;
            const sum = this.collaborators.reduce(
                (acc, u) => acc + (Number(u.progressPercent) || 0),
                0,
            );
            return Math.round(sum / this.collaborators.length);
        },
        breadcrumbs() {
            const crumbs = [
                { label: this.t.structure.title, to: '/dashboard/corporativo/estrutura' },
            ];
            if (this.role?.sectorId) {
                crumbs.push({
                    label: this.role.sectorName || this.t.structure.types.sector,
                    to: `/dashboard/corporativo/setores/${this.role.sectorId}`,
                });
            }
            crumbs.push({ label: this.role?.name || this.t.roleDetail.title });
            return crumbs;
        },
        layoutLabels() {
            const d = this.t.roleDetail;
            const s = this.t.structure;
            const c = this.t.collaborators;
            const sd = this.t.sectorDetail;
            return {
                back: sd.backShort,
                overviewTab: sd.overviewTab,
                collaboratorsTab: sd.collaborators,
                rolesTab: sd.statRoles,
                coursesTab: sd.coursesTab,
                active: s.active,
                inactive: s.inactive,
                manager: sd.manager,
                sector: d.sector,
                peopleWord: sd.peopleWord,
                peopleWordSingular: sd.peopleWordSingular,
                rolesWord: sd.rolesWord,
                rolesWordSingular: sd.rolesWordSingular,
                coursesWord: sd.coursesWord,
                coursesWordSingular: sd.coursesWordSingular,
                activePlural: sd.activePlural,
                activeSingular: sd.activeSingular,
                progressSuffix: sd.progressSuffix,
                avgCompletion: d.statCompletion,
                withProgress: sd.withProgress,
                filterActive: c.active,
                filterInactive: c.inactive,
                filterAll: this.t.common.filterAll,
                inactiveRole: sd.inactiveRole,
                inactiveRoles: sd.inactiveRoles,
                allRolesActive: sd.allRolesActive,
                noCourses: sd.noCourses,
                assignedCourses: sd.statCoursesAssigned,
                sectorProgress: d.roleProgress,
                roleProgress: d.roleProgress,
                inProgress: d.inProgress,
                completed: d.completed,
                overdue: sd.overdue,
                performance: d.performance,
                performanceRole: d.performance,
                overallProgress: sd.overallProgress,
                notStarted: sd.notStarted,
                attention: sd.attention,
                attentionLead: sd.attentionLead,
                attentionEmpty: sd.attentionEmpty,
                seeDetails: sd.seeDetails,
                seeAll: sd.seeAll,
                searchPeople: sd.searchPeople,
                allRoles: sd.allRoles,
                roleCol: c.role,
                statusCol: c.status,
                progressCol: c.progress,
                peopleEmptyTitle: d.collaboratorsEmpty,
                rolesEmptyTitle: sd.rolesEmptyTitle,
                rolesEmptyLead: sd.rolesEmptyLead,
                coursesEmptyTitle: d.coursesEmptyTitle,
                coursesEmptyLead: d.coursesEmptyLead,
                courseStatusDone: this.t.detail.statusCompleted,
                courseStatusProgress: this.t.detail.statusInProgress,
                courseStatusIdle: this.t.detail.statusNotStarted,
            };
        },
    },
    watch: {
        '$route.params.id': {
            immediate: true,
            handler() {
                this.load();
            },
        },
    },
    methods: {
        goBack() {
            if (this.$route.query.from === 'estrutura') {
                this.$router.push('/dashboard/corporativo/estrutura');
                return;
            }
            if (this.role?.sectorId && this.$route.query.from === 'setor') {
                this.$router.push(`/dashboard/corporativo/setores/${this.role.sectorId}`);
                return;
            }
            this.$router.push('/dashboard/corporativo/cargos');
        },
        goSector(id) {
            this.$router.push(`/dashboard/corporativo/setores/${id}`);
        },
        goCollaborator(id) {
            this.$router.push(`/dashboard/corporativo/colaboradores/${id}`);
        },
        async load() {
            const id = this.$route.params.id;
            if (!id) return;
            this.loading = true;
            this.error = false;
            try {
                const [role, usersRes, stats] = await Promise.all([
                    rolesApi.getById(this.token, id),
                    usersApi.list(this.token, { roleId: id, size: 500 }),
                    rolesApi.getStats(this.token, id).catch(() => null),
                ]);

                if (!role) {
                    this.error = 'not_found';
                    return;
                }

                this.role = {
                    ...role,
                    sectorId: stats?.sectorId || role.sectorId,
                    sectorName: stats?.sectorName || role.sectorName,
                };
                this.collaborators = usersRes.content || [];
                this.stats = stats;
                this.courseRows = Array.isArray(stats?.courses)
                    ? stats.courses
                    : aggregateCourses(this.collaborators);
            } catch (e) {
                console.error(e);
                this.error = e?.code === 'NOT_FOUND' ? 'not_found' : true;
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
